/**
 * meetingTelemetry — one registry for meeting/AV telemetry event names, plus
 * the helpers that keep thrown errors safe for telemetry.
 *
 * Track ids are emitted raw, not hashed: they are already stored raw elsewhere
 * and the raw value is what cross-references a row against LiveKit's own
 * records. Only error messages get sanitized, because a DOMException message
 * can carry a raw device label.
 *
 * Why this exists: event names were scattered string literals across the
 * meeting cubits and sinks, and the GA and Sentry names for the same condition
 * had already drifted (Sentry emits `meeting_speech_unreachable`, GA emits
 * `meeting_speech_reachability_problem_escalated` for the same escalation).
 * New meeting/AV events reference a constant here instead of a literal, so
 * there is one list to audit and one place to keep the names consistent.
 *
 * Two groups below:
 * - EXISTING: names already emitted in the codebase. Registered here so the
 *   registry is the single source of truth. Do NOT rename these; downstream
 *   GA/BigQuery queries and the admin inspector already key on them.
 * - NEW: the gaps the AV telemetry plan fills. Add a constant here before you
 *   emit it.
 *
 * See docs/prds/av-live-meeting-telemetry-plan.md.
 */
export const MeetingTelemetryEvent = {
  // --- EXISTING local-track lifecycle (already emitted; do not rename) ---
  /**
   * A local track (audio or video) was published to the room. Carries
   * `room_state` at publish time, `published_during_reconnect`,
   * `reconnect_count` and, for audio, `trigger` (who asked for the publish:
   * `connected`, `isolated_muted_publish`, `unmute`, `toggle_audio`,
   * `reconnect_room`, `audio_track_missing_rejoin`, `dead_air_nudge`,
   * `media_troubleshooter`, or `unknown` when nothing noted it, which is what a
   * publish LiveKit made on its own looks like).
   */
  localTrackPublished: 'meeting_local_track_published',
  /** The local audio track specifically was published. Same context fields. */
  audioTrackPublished: 'meeting_audio_track_published',
  /**
   * The join-time grace timer expired with no local audio published. Carries
   * `room_state`, `reconnect_count`, `seconds_since_connected`, `mic_muted`,
   * `had_publication` and `last_publish_error` (`stage:ErrorName` of the last
   * failed create/publish, if any).
   */
  audioTrackFailed: 'meeting_audio_track_failed',
  /** A local track was unpublished. */
  localTrackUnpublished: 'meeting_local_track_unpublished',
  /** The last local audio publication dropped to zero mid-session. */
  audioTrackLost: 'meeting_audio_track_lost',

  // --- EXISTING room connection lifecycle (already emitted; do not rename) ---
  /**
   * The room emitted `disconnected`. Carries `reason` (the LiveKit
   * `DisconnectReason` name), `page_leaving`, `document_hidden`,
   * `navigator_online`, `client_reconnect_in_flight`,
   * `seconds_since_last_reconnecting`, `reconnect_count`,
   * `had_audio_publication` (a mic was published at some point this session)
   * and `audio_publication_present` (one is still published now; LiveKit
   * unpublishes local tracks before it emits this, so expect false).
   */
  roomDisconnected: 'room_disconnected',
  /**
   * The room emitted `reconnected`. Carries `reconnect_count`, `duration_ms`
   * and `attempts` for the episode that just closed, and the local mic's
   * state at that moment: `audio_publication_present`,
   * `audio_track_ready_state`, `audio_track_muted`.
   */
  roomReconnected: 'room_reconnected',
  /** `room.state` changed. Carries `state` and `previous_state`. */
  roomConnectionStateChanged: 'room_connection_state_changed',

  // --- NEW room connection lifecycle (the silent-disconnect gaps) ---
  /**
   * Once a minute while the room stays disconnected, at most 30 beats, via
   * `logEventPassive`. Carries `reason` (from the disconnect),
   * `client_reconnect_in_flight` (read when the disconnect landed), `beat`,
   * `seconds_disconnected`, `document_hidden`, `navigator_online`. Its absence
   * after a `room_disconnected` means the tab went away.
   */
  disconnectedHeartbeat: 'meeting_disconnected_heartbeat',
  /**
   * `room.state` entered `reconnecting` or `signalReconnecting`. `signal_only`
   * is the latter; `attempt` counts state entries within one episode (LiveKit
   * keeps the state flat across its own retries, so this is not its try
   * count).
   */
  roomReconnecting: 'room_reconnecting',
  /**
   * The room disconnected while a reconnect episode was open and the reason
   * was not CLIENT_INITIATED: LiveKit exhausted its policy, or the server
   * rejected the resume. Carries the disconnect `reason`, `duration_ms` and
   * `attempts` of the episode, and `client_reconnect_in_flight`.
   */
  roomReconnectGaveUp: 'room_reconnect_gave_up',
  /**
   * This client called `disconnect()` while a reconnect episode was open
   * (`reconnectRoom` from the dead-air nudge or the troubleshooter, the QA
   * tool, a reload), cutting LiveKit's retries short. Same fields as
   * `room_reconnect_gave_up`; kept separate so that event stays a pure count
   * of the SDK giving up.
   */
  roomReconnectAborted: 'room_reconnect_aborted',
  /**
   * The user pressed a leave control inside the meeting. `source` says which:
   * `leave_dialog`, `exit_portal`, `av_check_back`. Logged before navigation
   * so the room_disconnected that follows can be excluded from the
   * silent-disconnect count without a time-window heuristic; the assignment
   * page's `room_leave` lands too late for that.
   */
  leaveClicked: 'meeting_leave_clicked',
  /**
   * The browser fired `offline` on `window`. Carries `room_state`. The
   * cheapest split between "their network dropped" and "the server dropped
   * them" when read next to `room_disconnected`.
   */
  networkOffline: 'meeting_network_offline',
  /** The browser fired `online`. Carries `seconds_offline`, `room_state`. */
  networkOnline: 'meeting_network_online',
  /**
   * A remote participant left the room, as seen by this client. The dropped
   * client's own events may never reach GA when its network is what died, so
   * the peers are the reliable record of when it happened. Carries
   * `participant_id_hash`, `is_server_participant` (moderator, secretary,
   * broadcast bot), `seconds_in_room` (from LiveKit's joinedAt, else this
   * client's own sighting), `remaining_participants`. livekit-client 2.15
   * does not expose the remote participant's disconnect reason.
   */
  participantDisconnected: 'meeting_participant_disconnected',

  // --- NEW media control failures (the dropped promises) ---
  // MeetingCubit.mute/unmute/enableVideo/disableVideo dropped the LiveKit
  // promise, so a rejected setMicrophoneEnabled (OverconstrainedError,
  // NotReadableError, NotAllowedError on a restart) was never recorded. Each
  // carries `error_name`, `error_message_sanitized`, `room_state`,
  // `device_id_hash` (the active device of that kind) and `track_ready_state`.
  /** setMicrophoneEnabled(true) rejected. */
  unmuteFailed: 'meeting_unmute_failed',
  /** setMicrophoneEnabled(false) rejected. */
  muteFailed: 'meeting_mute_failed',
  /** setCameraEnabled rejected; `action` is `toggle`, `enable` or `disable`. */
  videoToggleFailed: 'meeting_video_toggle_failed',
  /**
   * A device switch from the media settings dialog rejected. `kind` is
   * `audioinput`, `audiooutput` or `videoinput`; `device_id_hash` is the
   * device that was asked for. Only NotAllowedError had a handler before.
   * `trigger` is `devicechange` (our handler picked a replacement mic) or
   * `dialog` (the user picked one) when the mic switch itself rejected.
   */
  mediaDeviceSwitchFailed: 'media_device_switch_failed',

  // --- NEW local-track lifecycle (gaps the plan fills) ---
  /** About to call createLocalAudioTrack (the browser getUserMedia path). */
  localAudioTrackCreateAttempted: 'local_audio_track_create_attempted',
  /** createLocalAudioTrack resolved with a track. */
  localAudioTrackCreated: 'local_audio_track_created',
  /** createLocalAudioTrack threw: the browser never yielded a mic track. */
  localAudioTrackCreateFailed: 'local_audio_track_create_failed',
  /** About to publish the local audio track to LiveKit. */
  localAudioTrackPublishAttempted: 'local_audio_track_publish_attempted',
  /**
   * A local audio publish failed. `failure_stage: 'publish'` is a clean LiveKit
   * publish rejection (we already had a track). `failure_stage:
   * 'set_microphone_enabled'` is the combined getUserMedia+publish path, so it
   * mixes create and publish failures — discriminate with `error_name`
   * (`NotAllowedError`/`NotFoundError` => create/permission, not LiveKit).
   */
  localAudioTrackPublishFailed: 'local_audio_track_publish_failed',
  /**
   * The underlying MediaStreamTrack ended (device unplugged, revoked). Carries
   * `device_id_hash`, `document_hidden`, `track_muted`, `room_state`.
   */
  localAudioTrackEnded: 'local_audio_track_ended',
  /**
   * LiveKit re-acquired the mic (its own restart after `ended`, a device
   * switch, or an unmute on a dead capture). Carries `previous_track_id`,
   * `track_id`, `device_id_hash`, `room_state`. A restart during a reconnect
   * is the candidate for a re-acquire the SFU never hears.
   */
  localAudioTrackRestarted: 'local_audio_track_restarted',
  /**
   * The active audio input was switched without the user asking. Today only
   * `reason: 'device_vanished'` (the active input left the device list).
   */
  audioInputSwitched: 'meeting_audio_input_switched',
  /** Periodic, rate-limited local audio health sample during a session. */
  localAudioHealthSample: 'local_audio_health_sample',
  /**
   * Unmute hit a stale pinned device id (Safari `OverconstrainedError` after
   * an unplug) and the browser default mic worked instead.
   * `failed_device_id_hash` is the pinned id, `device_id_hash` the one we
   * landed on. `fallback_path` is `restart` (the track restarted on the
   * default mic) or `retry_no_track` (no track was published, so unmute was
   * simply retried and no device changed).
   */
  localAudioDeviceFallback: 'local_audio_device_fallback',
  /** The fallback to the default mic failed too; the repair dialog opens. */
  localAudioDeviceFallbackFailed: 'local_audio_device_fallback_failed',

  // --- AV check + repair/rejoin (ties to the rejoin blank-screen bug) ---
  // Note: AV-check start/capture already emit under room_avcheck_* names.
  /** The AV check passed and the user continued into the meeting. */
  avCheckPassed: 'av_check_passed',
  /** The user clicked rejoin after repairing their mic. */
  avRepairRejoinClicked: 'av_repair_rejoin_clicked',
  /** The rejoin repair (reconnect + republish on default) succeeded. */
  avRepairCompleted: 'av_repair_completed',
  /** The rejoin repair threw before the user was back with a working mic. */
  avRepairRejoinFailed: 'av_repair_rejoin_failed',

  // --- Duplicate identity (meeting opened in a second browser/tab) ---
  /**
   * LiveKit kicked this browser with DUPLICATE_IDENTITY because a second
   * browser/tab joined on the same identity. This is the kick that used to pop
   * the mic-missing dialog and start the Rejoin loop (BL-4892).
   */
  duplicateIdentityKick: 'meeting_duplicate_identity_kick',
  /**
   * The terminal duplicate-identity error screen was rendered to the user (the
   * one whose only action is a page reload).
   */
  duplicateIdentityScreenShown: 'meeting_duplicate_identity_screen_shown',

  // --- Content / slide video (distinct from LiveKit audio) ---
  // Each event carries an `attempt_id`: a client-side id for one playback of one
  // source, minted on loadstart. The client emits raw, timestamped media events
  // only; sessionizing (did it complete or get abandoned, total stalled time per
  // attempt) is done in SQL by grouping on `attempt_id`. Alert on the summed
  // `stall_duration_ms`, never on a raw stall event count.
  /** The deck video began loading a source. Mints a new `attempt_id`. */
  contentVideoLoadAttempted: 'content_video_load_attempted',
  /**
   * The deck video reported it can play. Note: `canplay` can fire more than
   * once per source (after a seek or a buffer refill), so this is not a
   * one-row-per-successful-load counter. The first canplay of an attempt also
   * carries `time_to_canplay_ms` (loadstart -> first canplay).
   */
  contentVideoCanPlay: 'content_video_can_play',
  /** play() rejected (non-autoplay) or the element errored. */
  contentVideoPlayFailed: 'content_video_play_failed',
  /**
   * A stall started: the video fired `waiting`. Once per stall (a stall stays
   * open until it ends).
   */
  contentVideoBuffering: 'content_video_buffering',
  /**
   * A stall ended. Carries `stall_duration_ms` (this stall only; SQL sums them
   * per attempt) and `recovered`: true if playback resumed, false if the attempt
   * ended mid-stall (slide change / unmount) — a wedged-then-abandoned stall so
   * its lost time still counts.
   */
  contentVideoStallEnded: 'content_video_stall_ended',
  /** The browser is fetching but data stopped arriving (network trouble). */
  contentVideoStalled: 'content_video_stalled',
  /** play() was blocked by the browser autoplay policy. */
  contentVideoAutoplayBlocked: 'content_video_autoplay_blocked',
  /**
   * The deck video played to the end. Carries `dropped_video_frames` and
   * `total_video_frames` where the browser supports them.
   */
  contentVideoEnded: 'content_video_ended',

  // --- Content / slide images (exhibits, image slides) ---
  // Same "raw state" intent as the video events: a "the exhibit didn't load"
  // report should be legible without a repro. Scoped to meeting content images
  // (namespace 'meeting'), not every avatar.
  /** A meeting content image began fetching. */
  contentImageLoadAttempted: 'content_image_load_attempted',
  /** A meeting content image fetched successfully. */
  contentImageLoaded: 'content_image_loaded',
  /** A meeting content image failed to load after retries. */
  contentImageLoadFailed: 'content_image_load_failed',
} as const

export type MeetingTelemetryEventName =
  (typeof MeetingTelemetryEvent)[keyof typeof MeetingTelemetryEvent]

/**
 * The fields `MeetingCubit.logEvent` stamps on every meeting event. New event
 * params are merged on top of these; they are documented here so a reader knows
 * what every meeting row already carries without re-reading the cubit.
 */
export interface MeetingTelemetryCommonFields {
  room_id: string
  assignment_id: string
  section_id: string
  slide_deck_id: string
  active_slide: number
}

// LiveKit's protocol enum values are stable wire identifiers. Keep the names
// local so this telemetry helper does not pull the LiveKit runtime into the
// repository chunk; newer values retain their number through the fallback.
const disconnectReasonNames: ReadonlyArray<string> = [
  'UNKNOWN_REASON',
  'CLIENT_INITIATED',
  'DUPLICATE_IDENTITY',
  'SERVER_SHUTDOWN',
  'PARTICIPANT_REMOVED',
  'ROOM_DELETED',
  'STATE_MISMATCH',
  'JOIN_FAILURE',
  'MIGRATION',
  'SIGNAL_CLOSE',
  'ROOM_CLOSED',
  'USER_UNAVAILABLE',
  'USER_REJECTED',
  'SIP_TRUNK_FAILURE',
  'CONNECTION_TIMEOUT',
  'MEDIA_FAILURE',
  'AGENT_ERROR',
]

/**
 * disconnectReasonName — the LiveKit `DisconnectReason` enum name
 * (`SIGNAL_CLOSE`, `STATE_MISMATCH`, ...) for the `reason` field. The enum is
 * numeric; a raw number would be unreadable in GA and would silently change
 * meaning on a protocol bump. An unknown number keeps its value in the name so
 * a newer SDK can never collapse into `UNKNOWN_REASON`.
 */
export function disconnectReasonName(reason: number | undefined): string {
  if (reason === undefined) return 'UNDEFINED'
  return disconnectReasonNames[reason] ?? `UNMAPPED_${reason}`
}

/**
 * hashId — a short, stable, non-reversible hash for ids that must be joinable
 * across events but never readable (device ids, participant ids). Synchronous
 * so it can run inside an event handler; cyrb53 with a fixed salt, rendered as
 * 11 base-36 characters. Not cryptographic: a device id is an opaque
 * per-origin token already, the hash only keeps the raw token out of GA.
 */
export function hashId(value: string | null | undefined): string | undefined {
  if (!value) return undefined
  const seed = 0x2f6e2b1
  let h1 = 0xdeadbeef ^ seed
  let h2 = 0x41c6ce57 ^ seed
  for (let i = 0; i < value.length; i++) {
    const ch = value.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  const n = 4294967296 * (2097151 & h2) + (h1 >>> 0)
  return n.toString(36).padStart(11, '0')
}

/** `document.hidden`, or undefined outside a browser. */
export function documentHidden(): boolean | undefined {
  return typeof document !== 'undefined' ? document.hidden : undefined
}

/**
 * `navigator.onLine`, or undefined where the browser does not report it. It is
 * the cheapest split between "their network dropped" and "the server dropped
 * them", so every connection event carries it.
 */
export function navigatorOnline(): boolean | undefined {
  if (typeof navigator === 'undefined') return undefined
  return typeof navigator.onLine === 'boolean' ? navigator.onLine : undefined
}

/** Whole seconds since `at`, or undefined when nothing has been recorded. */
export function secondsSince(
  at: number | null | undefined,
  now = Date.now()
): number | undefined {
  if (at === null || at === undefined) return undefined
  return Math.round((now - at) / 1000)
}

/**
 * sanitizeErrorMessage — reduce a thrown error to a bounded, length-capped
 * message string. This caps length only; it does not redact content, so a short
 * device label (`Microphone (Blue Yeti)`) still passes through. Pair it with
 * {@link errorName}: the error name is the low-cardinality, label-free field to
 * group and alert on, and the message is a best-effort, size-limited detail.
 * Do not treat the message as guaranteed free of hardware labels.
 */
export function sanitizeErrorMessage(
  error: unknown,
  maxLength = 120
): string | undefined {
  if (error === null || error === undefined) return undefined
  let message: string
  if (error instanceof Error) {
    message = error.message
  } else if (typeof error === 'string') {
    message = error
  } else if (
    // MediaError, DOMException-likes: not Error instances but carry a message.
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    message = error.message
  } else {
    message = String(error)
  }
  if (!message) return undefined
  return message.slice(0, maxLength)
}

/**
 * errorName — the DOMException / Error name (`NotAllowedError`,
 * `NotFoundError`, ...), which is the low-cardinality, safe part of a thrown
 * media error and the field worth grouping on.
 */
export function errorName(error: unknown): string | undefined {
  if (error instanceof Error) return error.name
  // DOMException / MediaError-likes carry a name but aren't Error instances.
  if (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    typeof error.name === 'string'
  ) {
    return error.name
  }
  return undefined
}
